import { useEffect, useState, FormEvent } from "react";
import { Plus, Trash2, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { EmptyState } from "@/components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { full_name: "", relationship: "Parent", age: "", city: "Harare", suburb: "", address: "", phone: "", care_needs: "", languages: "Shona, English" };
const field = "h-11 w-full rounded-xl border border-brand-line bg-white px-3 text-sm outline-none ring-brand-blue/30 focus:ring-4";

export function Recipients() {
  const [items, setItems] = useState<any[]>([]);
  const [cities, setCities] = useState<string[]>(["Harare"]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/recipients").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/meta/cities").then((r) => setCities(r.data.cities)); }, []);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/recipients", { ...form, age: form.age ? Number(form.age) : null });
      toast.success(`${form.full_name} added`);
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const remove = async (r: any) => {
    await api.delete(`/recipients/${r.recipient_id}`);
    toast.success("Removed"); load();
  };

  return (
    <section data-testid="recipients-section">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold text-brand-navy">Your care recipients</h2>
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-brand-blue px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-blueHover" data-testid="add-recipient-button"><Plus className="h-4 w-4" />Add recipient</button>
      </div>

      {items.length === 0 ? (
        <div className="mt-6"><EmptyState testId="recipients-empty" title="No one added yet" body="Add a parent, grandparent or relative in Zimbabwe so we know who to care for and where." /></div>
      ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {items.map((r) => (
            <article key={r.recipient_id} className="rounded-2xl border border-brand-line bg-white p-6 shadow-subtle" data-testid="recipient-card">
              <div className="flex items-start justify-between">
                <div><p className="font-heading text-lg font-semibold text-brand-navy" data-testid="recipient-name">{r.full_name}</p><p className="text-sm text-brand-slate">{r.relationship}{r.age ? ` · ${r.age} yrs` : ""}</p></div>
                <button onClick={() => remove(r)} className="rounded-full p-2 text-brand-slate transition hover:bg-red-50 hover:text-red-600" data-testid="delete-recipient-button" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-brand-slate">
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-emerald" />{[r.suburb, r.city].filter(Boolean).join(", ")}</p>
                {r.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-brand-emerald" />{r.phone}</p>}
                {r.care_needs && <p className="pt-2 text-brand-navy">{r.care_needs}</p>}
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="add-recipient-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add a care recipient</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
            <input required placeholder="Full name" className={`${field} sm:col-span-2`} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="recipient-name-input" />
            <select className={field} value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} data-testid="recipient-relationship-select">
              {["Parent", "Grandparent", "Sibling", "Spouse", "Aunt/Uncle", "Other"].map((o) => <option key={o}>{o}</option>)}
            </select>
            <input type="number" min={0} max={120} placeholder="Age" className={field} value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="recipient-age-input" />
            <select className={field} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="recipient-city-select">{cities.map((c) => <option key={c}>{c}</option>)}</select>
            <input placeholder="Suburb (e.g. Avondale)" className={field} value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} data-testid="recipient-suburb-input" />
            <input placeholder="Street address" className={`${field} sm:col-span-2`} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="recipient-address-input" />
            <input placeholder="Phone in Zimbabwe (+263…)" className={field} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="recipient-phone-input" />
            <input placeholder="Languages spoken" className={field} value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} data-testid="recipient-languages-input" />
            <textarea placeholder="Care needs & notes (mobility, conditions, routines)" rows={3} className="w-full rounded-xl border border-brand-line bg-white p-3 text-sm outline-none ring-brand-blue/30 focus:ring-4 sm:col-span-2" value={form.care_needs} onChange={(e) => setForm({ ...form, care_needs: e.target.value })} data-testid="recipient-needs-input" />
            <button type="submit" disabled={saving} className="h-11 rounded-xl bg-brand-blue text-sm font-semibold text-white transition hover:bg-brand-blueHover disabled:opacity-60 sm:col-span-2" data-testid="recipient-save-button">{saving ? "Saving…" : "Save recipient"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
